<template>
  <div>
    <!-- The spot flow's quick-filter block, markup for markup (FIX4-2 point 2). -->
    <div class="row g-2 mt-2">
      <div class="col-auto">
        <label class="form-label mb-1">
          {{ $t("forwards.picker.criteria.quick_filters") }}
        </label>
        <div class="d-flex gap-2 flex-wrap" data-testid="picker-quick-filters">
          <button
            type="button"
            class="btn btn-sm"
            :class="standardAibGc ? 'btn-primary' : 'btn-outline-secondary'"
            @click="toggleStandardAibGc"
          >
            {{ $t("forwards.picker.criteria.quick_aib_gc") }}
          </button>
          <button
            v-for="preset in TECHNOLOGY_PRESETS"
            :key="preset.key"
            type="button"
            class="btn btn-sm"
            :class="
              isTechnologyPreset(preset.key)
                ? 'btn-primary'
                : 'btn-outline-secondary'
            "
            @click="toggleTechnologyPreset(preset.key)"
          >
            {{ $t(`forwards.picker.criteria.quick_${preset.key}`) }}
          </button>
          <button
            type="button"
            class="btn btn-sm btn-outline-secondary"
            @click="codCalculatorOpen = true"
          >
            <FontAwesomeIcon icon="fa-solid fa-calculator" class="me-1" />
            {{ $t("forwards.picker.criteria.cod_calculator") }}
          </button>
        </div>
      </div>
    </div>

    <!-- Seven equal columns, four then three: every column is exactly one label and one control
         tall, so the card ends on a straight line. That is also why the hints are `title`s rather
         than `form-text` — only some columns have one, and a wrapped hint is what made the grid
         ragged (FIX4-2 point 3, D-FIX42-04/05). -->
    <div class="row g-2 mt-2" data-testid="picker-criteria-grid">
      <div
        class="col-6 col-md-3"
        :title="$t('forwards.picker.criteria.countries_hint')"
      >
        <label class="form-label fs-10 mb-1" :for="countriesId">
          {{ $t("forwards.picker.criteria.countries") }}
        </label>
        <MultiSelectComponent
          v-model="countries"
          :options="ALL_COUNTRIES"
          :buttonId="countriesId"
          :placeholder="$t('forwards.picker.criteria.countries_all')"
          size="sm"
        />
      </div>

      <div class="col-6 col-md-3">
        <label class="form-label fs-10 mb-1" :for="technologiesId">
          {{ $t("forwards.picker.criteria.technologies") }}
        </label>
        <MultiSelectComponent
          v-model="technologies"
          :options="technologyOptions"
          :buttonId="technologiesId"
          :placeholder="$t('forwards.picker.criteria.technologies_all')"
          size="sm"
        />
      </div>

      <div
        class="col-6 col-md-3"
        :title="$t('forwards.picker.criteria.cod_hint')"
      >
        <label class="form-label fs-10 mb-1" :for="codFromId">
          {{ $t("forwards.picker.criteria.cod_from") }}
        </label>
        <input
          :id="codFromId"
          v-model="codFrom"
          type="date"
          class="form-control form-control-sm"
        />
      </div>

      <div
        class="col-6 col-md-3"
        :title="$t('forwards.picker.criteria.cod_hint')"
      >
        <label class="form-label fs-10 mb-1" :for="codUntilId">
          {{ $t("forwards.picker.criteria.cod_until") }}
        </label>
        <input
          :id="codUntilId"
          v-model="codUntil"
          type="date"
          class="form-control form-control-sm"
        />
      </div>

      <div class="col-6 col-md-3">
        <label class="form-label fs-10 mb-1" :for="supportId">
          {{ $t("forwards.picker.criteria.support") }}
        </label>
        <select
          :id="supportId"
          v-model="supportChoice"
          class="form-select form-select-sm"
        >
          <option value="any">
            {{ $t("forwards.picker.criteria.support_any") }}
          </option>
          <option value="unsupported">
            {{ $t("forwards.picker.criteria.support_no") }}
          </option>
        </select>
      </div>

      <div
        class="col-6 col-md-3"
        :title="$t('forwards.picker.criteria.ecolabels_hint')"
      >
        <label class="form-label fs-10 mb-1" :for="ecolabelsId">
          {{ $t("forwards.picker.criteria.ecolabels") }}
        </label>
        <MultiSelectComponent
          v-model="ecolabels"
          :options="ecolabelOptions"
          :buttonId="ecolabelsId"
          :placeholder="$t('forwards.picker.criteria.ecolabels_all')"
          size="sm"
          hideBulkActions
        />
      </div>

      <div
        class="col-6 col-md-3"
        :title="$t('forwards.picker.criteria.min_price_filter_hint')"
      >
        <label class="form-label fs-10 mb-1" :for="minPriceId">
          {{ $t("forwards.picker.criteria.min_price_filter") }}
        </label>
        <MwhInputComponent
          v-model="minPrice"
          :id="minPriceId"
          unit="€/MWh"
          size="sm"
          :step="0.01"
          :placeholder="$t('forwards.common.none')"
        />
      </div>
    </div>

    <PickerCodCalculatorModal
      v-model="codCalculatorOpen"
      :productionUntil
      @apply="codFrom = $event"
    />
  </div>
</template>

<script setup lang="ts">
import MultiSelectComponent from "@/components/common/MultiSelectComponent.vue";
import MwhInputComponent from "@/forwards2/components/MwhInputComponent.vue";
import { ECOLABEL_KEYS } from "@/forwards2/forwardsTypes";
import PickerCodCalculatorModal from "@/forwards2/picker/PickerCodCalculatorModal.vue";
import {
  ADVANCED_TECHNOLOGIES,
  HSW_TECHNOLOGIES,
  isStandardAibGc,
  TECHNOLOGY_PRESETS,
  toggledStandardAibGc,
  type EligibilityCriteriaForm,
  type TechnologyPresetKey,
} from "@/forwards2/picker/pickerForm";
import { technologyNameForCode } from "@/types/technology";
import { ALL_COUNTRIES } from "@/utils/countries";
import { isEqual } from "lodash-es";
import { computed, ref, useId } from "vue";
import { useI18n } from "vue-i18n";

/**
 * Which devices may serve the deal — the quick-filter chips and the eligibility grid, the second
 * and third rows of the picker's filter card under the production range.
 *
 * Everything here except the minimum-price filter (`v-model:minPrice`) is persisted as the deal's
 * eligibility criteria and holds for its whole life, which is why the sketch's column filters are
 * inputs here instead (plan Appendix D.1 row 1d). An empty list means unrestricted, so clearing the
 * countries field widens the deal rather than emptying it. The deal's name and description are not
 * criteria and live in their own card (`PickerDealCardComponent`).
 */
defineProps<{
  /** The production range's last quarter, which the COD calculator's delivery date defaults from. */
  productionUntil: string;
}>();

const form = defineModel<EligibilityCriteriaForm>({ required: true });
/** Narrows the take table only; never part of the deal. */
const minPrice = defineModel<number | null>("minPrice", { required: true });

const { t } = useI18n();

const countriesId = useId();
const technologiesId = useId();
const codFromId = useId();
const codUntilId = useId();
const supportId = useId();
const minPriceId = useId();
const ecolabelsId = useId();

const codCalculatorOpen = ref(false);

const countries = field("countries");
const technologies = field("technologies");
const codFrom = field("cod_from");
const codUntil = field("cod_until");
const ecolabels = field("ecolabels");

const supportChoice = computed<"any" | "unsupported">({
  get: () => (form.value.support === false ? "unsupported" : "any"),
  set: (choice) => {
    form.value = {
      ...form.value,
      support: choice === "unsupported" ? false : null,
    };
  },
});

// The option's value is the AIB code prefix the backend matches on; only its label is the
// technology's display name (RV-picker-01). Biomass and nuclear rarely sell inside HSW, so they
// are listed last and tagged rather than hidden: a hidden option that is still selected would
// filter the take table while the field reads "Any technology".
const technologyOptions = computed(() => [
  ...HSW_TECHNOLOGIES.map((prefix) => ({
    value: prefix,
    label: technologyNameForCode(prefix),
  })),
  ...ADVANCED_TECHNOLOGIES.map((prefix) => ({
    value: prefix,
    label: technologyNameForCode(prefix),
    description: t("forwards.picker.criteria.technologies_advanced"),
  })),
]);

const ecolabelOptions = computed(() =>
  ECOLABEL_KEYS.map((key) => ({
    value: key as string,
    label: t(`forwards.picker.ecolabel.${key}`),
  }))
);

const standardAibGc = computed(() => isStandardAibGc(form.value.countries));

const toggleStandardAibGc = () => {
  form.value = {
    ...form.value,
    countries: toggledStandardAibGc(form.value.countries),
  };
};

const isTechnologyPreset = (key: TechnologyPresetKey): boolean =>
  isEqual([...form.value.technologies].sort(), [...presetOf(key)].sort());

const toggleTechnologyPreset = (key: TechnologyPresetKey) => {
  const technologies = isTechnologyPreset(key) ? [] : [...presetOf(key)];
  form.value = { ...form.value, technologies };
};

function presetOf(key: TechnologyPresetKey): readonly string[] {
  return TECHNOLOGY_PRESETS.find((preset) => preset.key === key)!.technologies;
}

/** A two-way binding onto one field of the form model, which is replaced rather than mutated. */
function field<K extends keyof EligibilityCriteriaForm>(key: K) {
  return computed<EligibilityCriteriaForm[K]>({
    get: () => form.value[key],
    set: (value) => {
      form.value = { ...form.value, [key]: value };
    },
  });
}
</script>
